import { esMoneda, formatear } from '@mc/comercio'

/**
 * La tienda dicha en llano, para el panel (auditoría del 29-09-2026): los importes en su
 * moneda y no en céntimos, el nombre de la pasarela y no su identificador, y lo que se le
 * pregunta a quien cambia el estado de un pedido a mano. Funciones puras, con su prueba.
 */

/** Céntimos → «S/ 150,00», con las mismas cuentas que la tienda (`@mc/comercio`): un peso chileno no tiene céntimos. */
export const dinero = (centimos: unknown, moneda: unknown): string => {
  if (typeof centimos !== 'number') return '—'
  return esMoneda(moneda) ? formatear(centimos, moneda) : `${(centimos / 100).toFixed(2)} ${typeof moneda === 'string' ? moneda : ''}`.trim()
}

const PASARELAS: Record<string, string> = { culqi: 'Culqi', mercadopago: 'Mercado Pago', stripe: 'Stripe', prueba: 'la pasarela de prueba' }

/** El nombre de una pasarela para leerlo en una frase: el identificador guardado no se enseña. */
export const nombreDePasarela = (p: unknown): string => (typeof p === 'string' ? (PASARELAS[p] ?? p) : '')

type Pago = { estado?: null | string; pasarela?: null | string }

/**
 * Lo que se le pregunta a quien cambia el estado de un pedido a mano, antes de guardar: lo
 * que el formulario no enseña. Cancelar o marcar «reembolsado» devuelve las existencias
 * y **no** devuelve el dinero; lo cobrado por pasarela se devuelve con «Reembolsar» (el
 * núcleo rechaza el «reembolsado» a mano). `null`: no hay nada que preguntar.
 */
export const preguntaAlCambiarDeEstado = (antes: Record<string, unknown>, despues: Record<string, unknown>): null | string => {
  const de = String(antes.estado ?? '')
  const a = String(despues.estado ?? de)
  if (de === a) return null
  const pago = (antes.pago ?? {}) as Pago
  const porPasarela = pago.estado === 'pagado' && Boolean(pago.pasarela)
  const cerrados = new Set(['cancelado', 'reembolsado'])
  const vuelven = antes.existenciasDescontadas !== false && !cerrados.has(de)
  if (a === 'reembolsado') {
    return porPasarela
      ? `Este pedido se cobró con ${nombreDePasarela(pago.pasarela)}: cambiar el estado no le devuelve nada al comprador, y no se va a guardar. Para devolverle el dinero, usa «Reembolsar».`
      : `¿Marcarlo como reembolsado? Esto no devuelve el dinero: devuélveselo tú por el medio con el que te pagó.${vuelven ? ' Las unidades vuelven a las existencias.' : ''}`
  }
  if (a === 'cancelado') {
    const dinero = porPasarela
      ? ` Ojo: cancelar no devuelve el dinero cobrado con ${nombreDePasarela(pago.pasarela)}; para eso, usa «Reembolsar».`
      : de !== 'pendiente-de-pago'
        ? ' Si ya te pagó, devuélvele el dinero tú.'
        : ''
    return `¿Cancelar el pedido?${vuelven ? ' Las unidades vuelven a las existencias.' : ''}${dinero}`
  }
  if (cerrados.has(de) && !cerrados.has(a)) return '¿Reabrir el pedido? Sus unidades se vuelven a descontar de las existencias.'
  return null
}

/** Una devolución apuntada en el pedido (la escribe el núcleo: `pago.reembolsos`). */
export type DevolucionDelPedido = { importe?: null | number; fecha?: null | string; origen?: null | string; quien?: null | string; motivo?: null | string; idExterno?: null | string }

type PagoConDevoluciones = Pago & { importe?: null | number; devuelto?: null | number; reembolsos?: null | DevolucionDelPedido[] }

/**
 * Lo cobrado, lo devuelto y lo que queda, en céntimos, con la misma regla que el núcleo
 * (`devueltoDe`): un pedido reembolsado antes de que hubiera devoluciones parciales no tiene
 * la lista, y se devolvió todo.
 */
export const cuentasDelPago = (pago: PagoConDevoluciones | null | undefined, total: unknown): { cobrado: number; devuelto: number; queda: number } => {
  const cobrado = typeof pago?.importe === 'number' ? pago.importe : typeof total === 'number' ? total : 0
  const apuntado = (pago?.reembolsos ?? []).reduce((s, d) => s + (typeof d.importe === 'number' && d.importe > 0 ? d.importe : 0), 0)
  const devuelto = pago?.estado === 'reembolsado' ? Math.max(apuntado, cobrado) : Math.min(apuntado, cobrado)
  return { cobrado, devuelto, queda: Math.max(0, cobrado - devuelto) }
}

/** Quién hizo una devolución, para leerlo: «Ana, desde el panel» o «en Stripe». */
export const quienDevolvio = (d: DevolucionDelPedido, pasarela: unknown): string =>
  d.origen === 'panel' ? `${d.quien ? `${d.quien}, ` : ''}desde el panel` : `en ${nombreDePasarela(pasarela) || 'la pasarela'}`

/**
 * Lo que se le dice a quien devuelve dinero antes de confirmar (§23): cuánto, a quién y por
 * dónde, que no se puede deshacer, y qué pasa con el pedido y sus existencias. Un parcial **no**
 * toca las existencias: si el comprador devuelve producto, se ajustan a mano.
 */
export const consecuenciasDeDevolver = (o: {
  /** Un pedido (por omisión) o una reserva de la agenda, que no tiene existencias ni cambia de estado. */
  de?: 'pedido' | 'reserva'
  importe: number
  queda: number
  moneda: unknown
  pasarela: unknown
  comprador?: unknown
  estado?: unknown
  existenciasDescontadas?: unknown
}): { titulo: string; dinero: string; pedido: string; parcial: boolean } => {
  const parcial = o.importe < o.queda
  if (o.de === 'reserva') {
    const quienPago = typeof o.comprador === 'string' && o.comprador ? ` a ${o.comprador}` : ' a quien reservó'
    return {
      titulo: parcial ? `¿Devolver ${dinero(o.importe, o.moneda)} de esta reserva?` : `¿Devolver ${dinero(o.importe, o.moneda)}, todo lo que queda?`,
      dinero: `Se le pide a ${nombreDePasarela(o.pasarela) || 'la pasarela'} que devuelva ${dinero(o.importe, o.moneda)}${quienPago}. Una devolución no se puede deshacer.`,
      pedido: parcial
        ? `Es una devolución parcial. Quedarán ${dinero(o.queda - o.importe, o.moneda)} por devolver, y la reserva sigue como está.`
        : 'La reserva no cambia de estado: si no va a venir, cancélala aparte.',
      parcial,
    }
  }
  const quien = typeof o.comprador === 'string' && o.comprador ? ` a ${o.comprador}` : ' al comprador'
  const dineroTexto = `Se le pide a ${nombreDePasarela(o.pasarela) || 'la pasarela'} que devuelva ${dinero(o.importe, o.moneda)}${quien}. Una devolución no se puede deshacer.`
  const cerrado = o.estado === 'cancelado' || o.estado === 'reembolsado'
  const vuelven = o.existenciasDescontadas !== false && !cerrado
  return {
    titulo: parcial ? `¿Devolver ${dinero(o.importe, o.moneda)} de este pedido?` : `¿Devolver ${dinero(o.importe, o.moneda)}, todo lo que queda?`,
    dinero: dineroTexto,
    pedido: parcial
      ? `Es una devolución parcial: el pedido sigue como está y sus existencias no se tocan (si te devuelven producto, ajústalas tú en el producto). Quedarán ${dinero(o.queda - o.importe, o.moneda)} por devolver.`
      : `El pedido queda como «Reembolsado»${vuelven ? ' y sus unidades vuelven a las existencias' : ''}.`,
    parcial,
  }
}

/**
 * El abandono por paso de las métricas de un formulario, como lista, venga como venga: un
 * núcleo anterior lo mandaba como objeto (`{ "1": 3 }`) y el `.map` del panel tumbaba la
 * pantalla entera.
 */
export const abandonoComoLista = (valor: unknown): number[] => {
  if (Array.isArray(valor)) return valor.map((n) => Number(n) || 0)
  if (valor && typeof valor === 'object')
    return Object.entries(valor as Record<string, unknown>)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([, n]) => Number(n) || 0)
  return []
}
